import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#E2E8F0]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-baseline gap-2"
          onClick={() => setOpen(false)}
        >
          <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#0A192F] font-medium">
            Ervion
          </span>
          <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            Accounting & Advisory
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-sans text-[13px] tracking-[0.15em] uppercase link-gold transition-colors ${
                  isActive ? "text-[#0A192F]" : "text-slate-500 hover:text-[#0A192F]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-sans text-[12px] tracking-[0.2em] uppercase hover:bg-[#1E293B] transition-all hover:-translate-y-0.5"
          >
            Book Consultation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#0A192F]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-[#E2E8F0] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="block font-sans text-sm tracking-[0.15em] uppercase text-[#0A192F]"
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="block bg-[#0A192F] text-white text-center py-3 font-sans text-xs tracking-[0.2em] uppercase"
            data-testid="mobile-header-cta"
          >
            Book Consultation
          </Link>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#0A192F] text-slate-300 mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-4xl text-white tracking-tight">Ervion</span>
            <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
              Accounting & Advisory
            </span>
          </div>
          <p className="mt-6 max-w-md text-slate-400 font-sans leading-relaxed text-[15px]">
            Accounting & advisory services engineered for founders. We eliminate the burden of
            back-office demands so you can focus on innovation, growth, and impact.
          </p>
          <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#C5A880]">
            Ervion Accounting & Advisory Services
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-white font-sans text-xs uppercase tracking-[0.25em] mb-5">
            Navigate
          </h4>
          <ul className="space-y-3 font-sans text-sm text-slate-400">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-[#C5A880] transition-colors" data-testid={`footer-nav-${n.label.toLowerCase()}`}>
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-white font-sans text-xs uppercase tracking-[0.25em] mb-5">
            Contact
          </h4>
          <ul className="space-y-3 font-sans text-sm text-slate-400">
            <li>
              <span className="block text-[11px] uppercase tracking-[0.2em] text-slate-500">Email</span>
              <a
                href="mailto:erviongroupllc@gmail.com"
                className="text-slate-200 hover:text-[#C5A880]"
                data-testid="footer-email"
              >
                erviongroupllc@gmail.com
              </a>
            </li>
            <li>
              <span className="block text-[11px] uppercase tracking-[0.2em] text-slate-500">Phone</span>
              <span className="text-slate-200">+1 (857) 314-1535</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-sans text-slate-500">
          <p>© {new Date().getFullYear()} Ervion Accounting & Advisory Services. All rights reserved.</p>
          <p className="tracking-[0.2em] uppercase">Precision • Discretion • Discipline</p>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
